import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { cn } from '../../lib/utils';

export interface SearchableMultiSelectOption {
  value: string | number;
  label: string;
  description?: string;
}

export interface SearchableMultiSelectProps {
  options: SearchableMultiSelectOption[];
  selected: (string | number)[];
  onSelectionChange: (selected: (string | number)[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  loadingMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Container element for the popover portal - use this when inside a Dialog */
  portalContainer?: HTMLElement | null;
}

export function SearchableMultiSelect({
  options,
  selected,
  onSelectionChange,
  placeholder = 'Selecionar...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum item encontrado.',
  loading = false,
  loadingMessage = 'Carregando...',
  disabled = false,
  className,
  portalContainer
}: SearchableMultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const selectedOptions = React.useMemo(() => {
    return options.filter((opt) => selected.includes(opt.value));
  }, [options, selected]);

  const toggleOption = React.useCallback(
    (value: string | number) => {
      const newSelected = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      onSelectionChange(newSelected);
    },
    [selected, onSelectionChange]
  );

  const removeOption = React.useCallback(
    (value: string | number, e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectionChange(selected.filter((v) => v !== value));
    },
    [selected, onSelectionChange]
  );

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || loading}
            className="w-full justify-between h-auto min-h-10 font-normal"
          >
            <span className="text-muted-foreground truncate">
              {loading ? loadingMessage : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          container={portalContainer}
        >
          <Command shouldFilter>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.description || ''}`}
                      onSelect={() => toggleOption(option.value)}
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{option.label}</div>
                        {option.description && (
                          <div className="text-xs text-muted-foreground truncate">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected items as removable badges */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((option) => (
            <Badge
              key={option.value}
              variant="secondary"
              className="gap-1 pr-1 cursor-default"
            >
              <span className="truncate max-w-[150px]">{option.label}</span>
              <button
                type="button"
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-muted p-0.5"
                onClick={(e) => removeOption(option.value, e)}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remover {option.label}</span>
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
